/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Call, CallDirection, CallStatus } from '../typings';
import { getCallIConsAndStyles } from '../utils/get-call-icons-and-styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CallDurationTimer } from './CallDurationTimer';
import { ChipRoot, IconWrap, PulseRing } from '../styles';
import { useMaskedIdentity } from '../../../shared/hooks/use-masked-identity';

const ChipView = ({
  onSelect,
  call,
  isAnyCallActive,
  callIndex = 0,
}: {
  onSelect: (call: Call) => void;
  call: Call & { callStartTime?: number };
  isAnyCallActive: boolean;
  callIndex?: number;
}) => {
  const { bg, iconColor, icon, wabaLabelColor } = getCallIConsAndStyles(
    call.status,
    20,
    call.direction || CallDirection.INCOMING
  );

  const { nameToRender } = useMaskedIdentity({
    name: call.customerName,
    phoneNumber : call.customerNumber
  })

  return (
    <ChipRoot
      onClick={(e) => {
        e.stopPropagation();
        onSelect(call);
      }}
      css={css`
        margin-right: ${callIndex === 1 && isAnyCallActive ? '310px' : '0px'};
        border: ${call.status === CallStatus.IN_PROGRESS ? '1px solid #3D906D' : '1px solid #DADAE4'};
        background-color: ${call.status === CallStatus.END ? 'transparent' : bg};
        gap: 4px;
      `}
      sx={
        call.status === CallStatus.END
          ? {
              color: '#7a7a7a',
              borderColor: '#E0E1E4',
              backgroundColor: 'transparent',
              overflow: 'hidden',
              ':hover': {
                backgroundColor: 'transparent',
                borderColor: '#E0E1E4',
              },
              ':before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '50%',
                height: '100%',
                backgroundColor: '#D9D9D9',
                animation: 'fillIn 3s linear forwards',
                zIndex: -1,
              },
              '@keyframes fillIn': {
                from: {
                  width: '0%',
                },
                to: {
                  width: '100%',
                },
              },
            }
          : {}
      }
    >
      <Box position="relative" marginRight={'4px'}>
        <IconWrap
          aria-hidden="true"
          sx={{
            color: iconColor,
          }}
        >
          {icon}
        </IconWrap>
        {call.status === CallStatus.INITIATED && (
          <>
            <PulseRing delay={0} />
            <PulseRing delay={250} />
            <PulseRing delay={500} />
          </>
        )}
      </Box>

      <Tooltip title={nameToRender} placement="top">
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color:
              call.status === CallStatus.MISSED && call.direction == CallDirection.INCOMING
                ? '#EF5350'
                : wabaLabelColor,
            lineHeight: 1,
            fontSize: 14,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 180,
          }}
        >
          {nameToRender || 'Unknown'}
        </Typography>
      </Tooltip>

      {call.status === CallStatus.IN_PROGRESS && call.callStartTime && (
        <CallDurationTimer startTime={call.callStartTime} status={call.status} />
      )}
      {call.status === CallStatus.IN_PROGRESS && <KeyboardArrowDownIcon sx={{ color: wabaLabelColor }} />}
    </ChipRoot>
  );
};

export default ChipView;
